/**
 * ColorField value model + CSS serialization helpers.
 */

export type GradientKind = "linear" | "radial" | "conic";

export type ColorStop = {
    id: string;
    /** Hex `#rrggbb` */
    color: string;
    /** 0–100 */
    position: number;
};

export type SolidColorValue = {
    mode: "solid";
    hex: string;
};

export type GradientColorValue = {
    mode: "gradient";
    kind: GradientKind;
    /** Degrees — used by linear + conic */
    angle?: number;
    stops: ColorStop[];
};

export type ColorValue = SolidColorValue | GradientColorValue;

export type ColorFieldMode = "solid" | "gradient";

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const HEX6_RE = /^#?([0-9a-fA-F]{6})$/;

export function normalizeHex(input: string): string | null {
    const raw = String(input || "").trim();
    const m = raw.match(HEX_RE);
    if (!m) return null;
    let h = m[1];
    if (h.length === 3) {
        h = h
            .split("")
            .map(c => c + c)
            .join("");
    }
    return `#${h.toLowerCase()}`;
}

/** Full 6-digit hex only — safe for live typing (does not expand #RGB). */
export function normalizeHex6(input: string): string | null {
    const raw = String(input || "").trim();
    const m = raw.match(HEX6_RE);
    if (!m) return null;
    return `#${m[1].toLowerCase()}`;
}

export function createStopId(): string {
    return `stop_${Math.random().toString(36).slice(2, 9)}`;
}

export function defaultSolid(hex = "#eaf5e2"): SolidColorValue {
    return { mode: "solid", hex: normalizeHex(hex) || "#eaf5e2" };
}

export function defaultGradient(
    kind: GradientKind = "linear",
    fromHex = "#eaf5e2",
): GradientColorValue {
    const a = normalizeHex(fromHex) || "#eaf5e2";
    const b = "#cfe8b8";
    return {
        mode: "gradient",
        kind,
        angle: 135,
        stops: [
            { id: createStopId(), color: a, position: 0 },
            { id: createStopId(), color: b, position: 100 },
        ],
    };
}

export function solidFromValue(value: ColorValue): string {
    if (value.mode === "solid") return value.hex;
    return value.stops[0]?.color || "#eaf5e2";
}

/**
 * Pair for email theme export: rich CSS + solid hex fallback (first stop).
 */
export function colorValueToThemePair(value: ColorValue): {
    css: string;
    solid: string;
} {
    const solid = normalizeHex(solidFromValue(value)) || "#000000";
    const css = colorValueToCss(value);
    return { css, solid };
}

/** Serialize for CSS `background` / `color` / swatch. */
export function colorValueToCss(value: ColorValue): string {
    if (value.mode === "solid") {
        return normalizeHex(value.hex) || value.hex;
    }

    const stops = [...value.stops]
        .sort((x, y) => x.position - y.position)
        .map(
            s =>
                `${normalizeHex(s.color) || s.color} ${clamp(
                    s.position,
                    0,
                    100,
                )}%`,
        )
        .join(", ");

    const angle = value.angle ?? 135;

    if (value.kind === "radial") {
        return `radial-gradient(circle, ${stops})`;
    }
    if (value.kind === "conic") {
        return `conic-gradient(from ${angle}deg, ${stops})`;
    }
    return `linear-gradient(${angle}deg, ${stops})`;
}

/**
 * Best-effort parse of CSS gradient strings into our gradient model.
 * Supports linear / radial / conic with hex stops (+ optional %).
 */
export function parseCssGradient(input: string): GradientColorValue | null {
    const raw = String(input || "").trim();
    if (!raw) return null;

    const linear = raw.match(
        /^linear-gradient\(\s*(?:(-?\d+(?:\.\d+)?)deg\s*,\s*)?(.+)\)\s*$/i,
    );
    const radial = raw.match(
        /^radial-gradient\(\s*(?:circle(?:\s+at\s+[^,]+)?\s*,\s*)?(.+)\)\s*$/i,
    );
    const conic = raw.match(
        /^conic-gradient\(\s*(?:from\s+(-?\d+(?:\.\d+)?)deg\s*,\s*)?(.+)\)\s*$/i,
    );

    let kind: GradientKind = "linear";
    let angle = 135;
    let stopsBody = "";

    if (linear) {
        kind = "linear";
        if (linear[1] != null) angle = Number(linear[1]);
        stopsBody = linear[2];
    } else if (radial) {
        kind = "radial";
        stopsBody = radial[1];
    } else if (conic) {
        kind = "conic";
        if (conic[1] != null) angle = Number(conic[1]);
        stopsBody = conic[2];
    } else {
        return null;
    }

    const parts = splitGradientStops(stopsBody);
    const stops: ColorStop[] = [];

    for (let i = 0; i < parts.length; i += 1) {
        const part = parts[i].trim();
        if (!part) continue;
        // Skip non-color leading tokens like "to right", "at center"
        if (/^(to|at|circle|ellipse|closest|farthest)/i.test(part)) continue;

        const m = part.match(
            /^(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\))(?:\s+(-?\d+(?:\.\d+)?)%)?$/,
        );
        if (!m) continue;
        const color = normalizeCssColorToHex(m[1]);
        if (!color) continue;
        const position =
            m[2] != null
                ? clamp(Number(m[2]), 0, 100)
                : stops.length === 0
                ? 0
                : 100;
        stops.push({ id: createStopId(), color, position });
    }

    if (stops.length < 2) return null;
    // Cap at 5 stops for the editor
    const capped = stops.slice(0, 5);
    // Ensure unique ascending-ish positions if all defaulted
    if (capped.every((s, idx) => idx === 0 || s.position === 100)) {
        capped.forEach((s, idx) => {
            s.position = Math.round((idx / (capped.length - 1)) * 100);
        });
    }

    return {
        mode: "gradient",
        kind,
        angle: Number.isFinite(angle) ? clamp(angle, 0, 360) : 135,
        stops: capped,
    };
}

function splitGradientStops(body: string): string[] {
    const parts: string[] = [];
    let current = "";
    let depth = 0;
    for (const ch of body) {
        if (ch === "(") depth += 1;
        if (ch === ")") depth = Math.max(0, depth - 1);
        if (ch === "," && depth === 0) {
            parts.push(current);
            current = "";
            continue;
        }
        current += ch;
    }
    if (current.trim()) parts.push(current);
    return parts;
}

function normalizeCssColorToHex(input: string): string | null {
    const hex = normalizeHex(input);
    if (hex) return hex;
    const rgb = input.match(
        /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+)?\s*\)$/i,
    );
    if (rgb) {
        return rgbToHex(Number(rgb[1]), Number(rgb[2]), Number(rgb[3]));
    }
    return null;
}

/** Short label under the field trigger. */
export function colorValueToLabel(value: ColorValue): string {
    if (value.mode === "solid") {
        return (normalizeHex(value.hex) || value.hex).toUpperCase();
    }
    const css = colorValueToCss(value);
    return css.length > 42 ? `${css.slice(0, 40)}…` : css;
}

export function clamp(n: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, n));
}

/* ─── HEX ↔ HSV ───────────────────────────────────────────────────────── */

export type Hsv = { h: number; s: number; v: number };

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const n = normalizeHex(hex) || "#000000";
    return {
        r: parseInt(n.slice(1, 3), 16),
        g: parseInt(n.slice(3, 5), 16),
        b: parseInt(n.slice(5, 7), 16),
    };
}

export function rgbToHex(r: number, g: number, b: number): string {
    const to = (x: number) =>
        clamp(Math.round(x), 0, 255).toString(16).padStart(2, "0");
    return `#${to(r)}${to(g)}${to(b)}`;
}

export function hexToHsv(hex: string): Hsv {
    const { r, g, b } = hexToRgb(hex);
    const rr = r / 255;
    const gg = g / 255;
    const bb = b / 255;
    const max = Math.max(rr, gg, bb);
    const min = Math.min(rr, gg, bb);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === rr) h = ((gg - bb) / d) % 6;
        else if (max === gg) h = (bb - rr) / d + 2;
        else h = (rr - gg) / d + 4;
        h *= 60;
        if (h < 0) h += 360;
    }
    const s = max === 0 ? 0 : d / max;
    return { h, s, v: max };
}

export function hsvToHex(h: number, s: number, v: number): string {
    const hh = ((h % 360) + 360) % 360;
    const c = v * s;
    const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
    const m = v - c;
    let r = 0;
    let g = 0;
    let b = 0;
    if (hh < 60) [r, g, b] = [c, x, 0];
    else if (hh < 120) [r, g, b] = [x, c, 0];
    else if (hh < 180) [r, g, b] = [0, c, x];
    else if (hh < 240) [r, g, b] = [0, x, c];
    else if (hh < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];
    return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

/* ─── HEX ↔ HSL + input formats ───────────────────────────────────────── */

export type Hsl = { h: number; s: number; l: number };

/** Display / edit format in the solid panel. */
export type SolidColorFormat = "hex" | "rgb" | "hsl";

export const SOLID_COLOR_FORMATS: SolidColorFormat[] = ["hex", "rgb", "hsl"];

export function nextSolidColorFormat(
    current: SolidColorFormat,
): SolidColorFormat {
    const i = SOLID_COLOR_FORMATS.indexOf(current);
    return SOLID_COLOR_FORMATS[(i + 1) % SOLID_COLOR_FORMATS.length];
}

export function hexToHsl(hex: string): Hsl {
    const { r, g, b } = hexToRgb(hex);
    const rr = r / 255;
    const gg = g / 255;
    const bb = b / 255;
    const max = Math.max(rr, gg, bb);
    const min = Math.min(rr, gg, bb);
    const l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === rr) h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6;
    else if (max === gg) h = ((bb - rr) / d + 2) / 6;
    else h = ((rr - gg) / d + 4) / 6;
    return { h: h * 360, s, l };
}

function hue2rgb(p: number, q: number, t: number): number {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
}

export function hslToHex(h: number, s: number, l: number): string {
    const hh = (((h % 360) + 360) % 360) / 360;
    const ss = clamp(s, 0, 1);
    const ll = clamp(l, 0, 1);
    if (ss === 0) {
        const v = ll * 255;
        return rgbToHex(v, v, v);
    }
    const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
    const p = 2 * ll - q;
    return rgbToHex(
        hue2rgb(p, q, hh + 1 / 3) * 255,
        hue2rgb(p, q, hh) * 255,
        hue2rgb(p, q, hh - 1 / 3) * 255,
    );
}

export function seedGradientFromSolid(hex: string): GradientColorValue {
    return defaultGradient("linear", hex);
}
